import React, { useState, useEffect } from 'react';
import { Edit, Save, X, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YEARS = [2024, 2025, 2026];

// Groups — auto fields are computed (BALANCE all auto, TOTAL cols in each group auto)
const GROUPS = [
  {
    key: 'payable', label: 'PAYABLE', bg: 'bg-blue-100 text-blue-800', cellBg: '',
    fields: ['payableSL', 'payablePL', 'payableWO', 'payableHO', 'payableTotal'],
    auto: new Set(['payableTotal']),
  },
  {
    key: 'paid', label: 'PAID', bg: 'bg-orange-100 text-orange-800', cellBg: '',
    fields: ['paidSL', 'paidPL', 'paidWO', 'paidHO', 'paidTotal'],
    auto: new Set(['paidTotal']),
  },
  {
    key: 'balance', label: 'BALANCE = PAYABLE − PAID', bg: 'bg-purple-100 text-purple-800', cellBg: 'bg-purple-50/40',
    fields: ['balanceSL', 'balancePL', 'balanceWO', 'balanceHO', 'balanceTotal'],
    auto: new Set(['balanceSL', 'balancePL', 'balanceWO', 'balanceHO', 'balanceTotal']),
  },
];

const SUB_LABELS = ['SL', 'PL', 'WO', 'HO', 'TOTAL'];

const ATTENDANCE_FIELDS = [
  { key: 'present',   label: 'P' },
  { key: 'lateMark',  label: 'L' },
  { key: 'halfDay',   label: 'H' },
  { key: 'absent',    label: 'A' },
  { key: 'unplanned', label: 'U' },
];

// Compute derived TOTAL and BALANCE fields from the 8 base editable fields
const computeDerived = (row) => {
  const r = { ...row };
  const pSL = r.payableSL || 0, pPL = r.payablePL || 0, pWO = r.payableWO || 0, pHO = r.payableHO || 0;
  const dSL = r.paidSL    || 0, dPL = r.paidPL    || 0, dWO = r.paidWO    || 0, dHO = r.paidHO    || 0;
  r.payableTotal = pSL + pPL + pWO + pHO;
  r.paidTotal    = dSL + dPL + dWO + dHO;
  r.balanceSL    = pSL - dSL;
  r.balancePL    = pPL - dPL;
  r.balanceWO    = pWO - dWO;
  r.balanceHO    = pHO - dHO;
  r.balanceTotal = r.balanceSL + r.balancePL + r.balanceWO + r.balanceHO;
  return r;
};

function EditableNum({ val, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(String(val ?? 0));

  if (!editing) return (
    <span
      className="cursor-pointer underline decoration-dotted hover:text-stone-900 whitespace-nowrap"
      onClick={() => { setV(String(val ?? 0)); setEditing(true); }}
    >
      {val ?? 0}
    </span>
  );
  return (
    <input
      type="number"
      value={v}
      autoFocus
      className="w-14 text-center border border-blue-400 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); const n = Number(v); if (!isNaN(n)) onSave(n); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { setEditing(false); const n = Number(v); if (!isNaN(n)) onSave(n); }
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

export default function LeaveIndexTab() {
  const [year, setYear] = useState(2025);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const fetchData = async (y = year) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`${API}/hr/leave-index`, {
        params: { year: y },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((res.data.rows || []).map(computeDerived));
    } catch {
      toast.error('Failed to load Leave Index');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(year); }, [year]);

  const handleEdit = async (employeeId, field, value) => {
    // Optimistically update the edited field AND recompute all derived fields
    setRows(prev => prev.map(r =>
      r.employeeId === employeeId ? computeDerived({ ...r, [field]: value }) : r
    ));
    try {
      const token = localStorage.getItem('crm_token');
      await axios.patch(`${API}/hr/leave-index/update-cell`,
        { employeeId, year, field, value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      toast.error('Failed to save change');
      fetchData(year);
    }
  };

  const totalCols = 4 + 5 + 5 + 5 + 5; // fixed columns + 3 groups × 5 = 24

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-stone-600">Year:</span>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-stone-300 rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="leave-index-year-select"
            >
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-7 p-0"
              onClick={() => fetchData(year)}
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
          <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
            P / L / H / A / U — auto-synced from Monthly Attendance
          </span>
        </div>
        <Button
          size="sm"
          variant={editMode ? 'default' : 'outline'}
          className={`h-8 ${editMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
          onClick={() => setEditMode(m => !m)}
          data-testid="leave-index-edit-btn"
        >
          {editMode ? <><Save className="w-3.5 h-3.5 mr-1" />Done Editing</> : <><Edit className="w-3.5 h-3.5 mr-1" />Edit</>}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-stone-500">Loading Leave Index...</span>
        </div>
      ) : (
        <div className="overflow-auto rounded-lg border border-stone-200" style={{ maxHeight: 620 }}>
          <table className="text-xs border-collapse" style={{ minWidth: 1480 }}>
            <thead className="sticky top-0 z-20">
              {/* Title row */}
              <tr className="bg-yellow-400 text-stone-900">
                <th colSpan={totalCols} className="px-3 py-2 text-center text-sm font-extrabold tracking-wide border border-stone-700">
                  YEAR ATTENDANCE {year} SUMMARY &nbsp;—&nbsp; PAYABLE PAID BALANCE PER MONTH
                </th>
              </tr>
              {/* Group headers */}
              <tr className="bg-stone-100 text-stone-700 font-semibold">
                <th rowSpan={2} className="px-2 py-1.5 text-center border border-stone-300 w-10 sticky left-0 bg-stone-100 z-10">SR<br/>NO</th>
                <th rowSpan={2} className="px-2 py-1.5 text-left border border-stone-300 min-w-[150px] sticky bg-stone-100 z-10" style={{ left: 40 }}>NAME</th>
                <th rowSpan={2} className="px-2 py-1.5 text-left border border-stone-300 min-w-[130px]">DESIGNATION</th>
                <th rowSpan={2} className="px-2 py-1.5 text-center border border-stone-300 min-w-[90px]">DATE OF<br/>JOINING</th>
                <th colSpan={5} className="px-2 py-1.5 text-center border border-stone-300 bg-green-100 text-green-800">ATTENDANCE YEAR SUMMARY</th>
                {GROUPS.map(g => (
                  <th key={g.key} colSpan={5} className={`px-2 py-1.5 text-center border border-stone-300 ${g.bg}`}>{g.label}</th>
                ))}
              </tr>
              {/* Sub-labels */}
              <tr className="bg-stone-50 text-stone-600">
                {ATTENDANCE_FIELDS.map(f => (
                  <th key={f.key} className="px-2 py-1.5 text-center border border-stone-300 w-12 bg-green-50 text-green-800 font-bold">{f.label}</th>
                ))}
                {GROUPS.map(g => SUB_LABELS.map((sl, si) => {
                  const isAutoCol = si === 4; // TOTAL col is always auto in PAYABLE/PAID; all in BALANCE
                  const isBalance = g.key === 'balance';
                  return (
                    <th key={`${g.key}-${sl}`} className={`px-2 py-1.5 text-center border border-stone-300 w-12 font-bold ${isBalance || isAutoCol ? 'text-stone-400 italic' : ''}`}>
                      {sl}
                    </th>
                  );
                }))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-stone-50';
                const cell = `px-1.5 py-1 text-center border border-stone-200 text-stone-800`;
                return (
                  <tr key={row.employeeId} className={`${rowBg} hover:bg-blue-50/30 transition-colors`}>
                    <td className={`${cell} sticky left-0 ${rowBg} z-10 font-semibold`}>{row.srNo || i + 1}</td>
                    <td className={`px-2 py-1 text-left border border-stone-200 sticky ${rowBg} z-10 font-bold text-stone-800`} style={{ left: 40 }}>{row.employeeName}</td>
                    <td className={`${cell} text-left`}>{row.designation || '-'}</td>
                    <td className={`${cell} font-mono text-stone-500 text-[11px]`}>{row.joiningDate || '-'}</td>

                    {/* Attendance counts — always auto-computed, read-only */}
                    {ATTENDANCE_FIELDS.map(f => (
                      <td key={f.key} className={`${cell} bg-green-50/60 text-green-800 font-semibold`} title="Auto-synced from Monthly Attendance">
                        {row[f.key] ?? 0}
                      </td>
                    ))}

                    {/* Payable, Paid, Balance groups */}
                    {GROUPS.map(g => g.fields.map((fk, fi) => {
                      const isAuto = g.auto.has(fk);
                      const val = row[fk] ?? 0;
                      const isNeg = isAuto && val < 0;
                      return (
                        <td
                          key={fk}
                          title={isAuto ? (g.key === 'balance' ? 'Auto: PAYABLE − PAID' : 'Auto: sum') : undefined}
                          className={`${cell} ${g.cellBg} ${fi === 4 ? 'font-bold border-l-2 border-l-stone-300' : ''} ${isAuto ? 'text-stone-400 italic' : ''} ${isNeg ? '!text-red-500 font-bold' : ''}`}
                        >
                          {(editMode && !isAuto)
                            ? <EditableNum val={val} onSave={v => handleEdit(row.employeeId, fk, v)} />
                            : val}
                        </td>
                      );
                    }))}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="py-10 text-center text-stone-400 text-sm">No employees found</td>
                </tr>
              )}
            </tbody>
            {/* Footer totals */}
            <tfoot className="sticky bottom-0 bg-stone-200 z-10">
              <tr>
                <td colSpan={4} className="px-3 py-1.5 text-right text-xs font-extrabold text-stone-800 border border-stone-400">TOTAL</td>
                {ATTENDANCE_FIELDS.map(f => {
                  const t = rows.reduce((s, r) => s + (Number(r[f.key]) || 0), 0);
                  return <td key={f.key} className="px-1.5 py-1.5 text-center text-xs font-bold border border-stone-400 bg-green-50">{t || '-'}</td>;
                })}
                {GROUPS.map(g => g.fields.map((fk, fi) => {
                  const t = rows.reduce((s, r) => s + (Number(r[fk]) || 0), 0);
                  return <td key={fk} className={`px-1.5 py-1.5 text-center text-xs font-bold border border-stone-400 ${fi === 4 ? 'font-extrabold' : ''}`}>{t || '-'}</td>;
                }))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
